import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { WEBSITE_URL, WEBSITE_HOST } from '../lib/org';

const GOOGLE_REVIEW_URL = 'https://g.page/r/Ca-UR3Jy7WrTEBM/review';

// ── Palette (matches mockup) ──────────────────────────────────────────────────
const NAVY      = 'var(--accent)';
const TERRACOTTA = 'var(--copper)';
const CREAM     = 'var(--caera-ivory)';
const BORDER    = 'var(--accent-border)';
const BG        = 'var(--accent-bg)';

// ── Shared styles ─────────────────────────────────────────────────────────────
const phoneStyle = {
  background: BG,
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  fontFamily: 'Arial, sans-serif',
};

const textareaStyle = {
  width: '100%',
  border: `0.5px solid ${BORDER}`,
  borderRadius: 7,
  padding: '9px 11px',
  fontSize: 12,
  color: NAVY,
  background: 'var(--accent-bg)',
  fontFamily: 'inherit',
  resize: 'none',
  minHeight: 68,
  lineHeight: 1.5,
  boxSizing: 'border-box',
};

// ── Numbered star rating ──────────────────────────────────────────────────────
function StarRating({ value, onChange, disabled }) {
  return (
    <div style={{ display: 'flex', gap: 5 }}>
      {[1, 2, 3, 4, 5].map(n => (
        <button
          key={n}
          type="button"
          onClick={() => !disabled && onChange(n)}
          style={{
            width: 30, height: 30, borderRadius: 7, flexShrink: 0,
            border: `0.5px solid ${value >= n ? NAVY : BORDER}`,
            background: value >= n ? NAVY : 'var(--accent-bg)',
            color: value >= n ? CREAM : 'var(--text3)',
            fontSize: 14, fontWeight: 500,
            cursor: disabled ? 'default' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: 'inherit',
          }}>
          {n}
        </button>
      ))}
    </div>
  );
}

// ── Custom radio group ────────────────────────────────────────────────────────
function RadioGroup({ options, value, onChange, disabled }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {options.map(opt => (
        <label
          key={opt.val}
          style={{
            display: 'flex', alignItems: 'flex-start', gap: 10,
            fontSize: 12, color: NAVY,
            cursor: disabled ? 'default' : 'pointer', lineHeight: 1.4,
          }}
          onClick={() => !disabled && onChange(opt.val)}>
          <div style={{
            width: 17, height: 17, borderRadius: '50%', flexShrink: 0, marginTop: 1,
            border: `1.5px solid ${value === opt.val ? NAVY : BORDER}`,
            background: value === opt.val ? NAVY : 'var(--accent-bg)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {value === opt.val && (
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />
            )}
          </div>
          <span>{opt.label}</span>
        </label>
      ))}
    </div>
  );
}

// ── Section card with navy header ─────────────────────────────────────────────
function SectionCard({ icon, title, children }) {
  return (
    <div style={{ background: '#fff', borderRadius: 10, border: `0.5px solid ${BORDER}`, overflow: 'hidden' }}>
      <div style={{
        background: NAVY, padding: '10px 14px',
        fontSize: 12, fontWeight: 500, color: CREAM,
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <span style={{ fontSize: 14 }}>{icon}</span> {title}
      </div>
      <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 14 }}>
        {children}
      </div>
    </div>
  );
}

// ── Question block ────────────────────────────────────────────────────────────
function QBlock({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 500, color: NAVY, marginBottom: 8, lineHeight: 1.4 }}>
        {label}
      </div>
      {children}
    </div>
  );
}

// ── Status screens ────────────────────────────────────────────────────────────
function StatusScreen({ emoji, title, body }) {
  return (
    <div style={phoneStyle}>
      <div style={{ background: NAVY, padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <img src="/logo192.png" alt="" onError={e => { e.target.style.display = 'none'; }}
          style={{ width: 44, height: 44, borderRadius: 7, objectFit: 'cover', flexShrink: 0 }} />
        <div>
          <div style={{ fontSize: 14, fontWeight: 500, color: CREAM, lineHeight: 1.2 }}>A Heart For Care</div>
          <div style={{ fontSize: 9, color: 'rgba(235,227,216,0.5)', letterSpacing: '0.08em', marginTop: 2 }}>
            Home care services by Hardshoulder Group
          </div>
        </div>
      </div>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div style={{
          background: '#fff', borderRadius: 14, border: `0.5px solid ${BORDER}`,
          padding: '32px 24px', maxWidth: 380, width: '100%', textAlign: 'center',
        }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>{emoji}</div>
          <div style={{ fontSize: 17, fontWeight: 600, color: NAVY, marginBottom: 10 }}>{title}</div>
          <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6, marginBottom: 16 }}>{body}</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', borderTop: `0.5px solid ${BORDER}`, paddingTop: 14 }}>
            Questions? Call us at <strong style={{ color: NAVY }}>470-470-6640</strong>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Google G icon ─────────────────────────────────────────────────────────────
function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M21.8 12.2c0-.7-.1-1.4-.2-2H12v3.8h5.5c-.2 1.2-1 2.2-2 2.9v2.4h3.2c1.9-1.7 3.1-4.3 3.1-7.1z" fill="#4285F4"/>
      <path d="M12 22c2.7 0 5-.9 6.7-2.4l-3.2-2.5c-.9.6-2 1-3.5 1-2.7 0-5-1.8-5.8-4.3H2.9v2.5C4.6 19.9 8.1 22 12 22z" fill="#34A853"/>
      <path d="M6.2 13.8c-.2-.6-.3-1.2-.3-1.8s.1-1.2.3-1.8V7.7H2.9C2.3 9 2 10.5 2 12s.3 3 .9 4.3l3.3-2.5z" fill="#FBBC05"/>
      <path d="M12 7.6c1.5 0 2.8.5 3.9 1.5l2.9-2.9C17 4.6 14.7 3.6 12 3.6 8.1 3.6 4.6 5.7 2.9 8.8l3.3 2.5C7 9.4 9.3 7.6 12 7.6z" fill="#EA4335"/>
    </svg>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function QASurveyPublic() {
  const { token } = useParams();

  const [tokenStatus, setTokenStatus] = useState('loading'); // loading|valid|invalid|used|expired|submitted
  const [surveyMeta,  setSurveyMeta]  = useState(null);
  const [submitting,  setSubmitting]  = useState(false);
  const [submitError, setSubmitError] = useState('');

  const [formData, setFormData] = useState({
    score_overall:           null,
    answer_punctuality:      null,
    score_communication:     null,
    score_office_access:     null,
    answer_nurse_consistent: null,
    feedback_going_well:     '',
    feedback_improve:        '',
    answer_recommend:        null,
    clicked_google_review:   false,
  });

  // ── Load survey ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!token) { setTokenStatus('invalid'); return; }
    (async () => {
      try {
        const res  = await fetch(
          `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/get-qa-survey-data`,
          { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token }) }
        );
        const data = await res.json();
        if (!data.success) { setTokenStatus(data.error || 'invalid'); return; }
        setSurveyMeta(data.survey);
        setTokenStatus('valid');
      } catch {
        setTokenStatus('invalid');
      }
    })();
  }, [token]);

  // ── Submit ────────────────────────────────────────────────────────────────
  // withReview=true: opens Google tab before the async submit (avoids popup blocker)
  const handleSubmit = async (withReview = false) => {
    if (!formData.score_overall) {
      setSubmitError('Please rate your overall satisfaction before submitting.');
      return;
    }
    if (!formData.answer_recommend) {
      setSubmitError('Please let us know if you would recommend A Heart For Care.');
      return;
    }
    setSubmitError('');

    // Open Google review tab NOW — synchronous, within the click handler,
    // so browsers don't treat it as a blocked popup.
    if (withReview) {
      window.open(GOOGLE_REVIEW_URL, '_blank', 'noopener,noreferrer');
    }

    setSubmitting(true);
    try {
      const res  = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/submit-qa-response`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, response: { ...formData, clicked_google_review: withReview } }),
        }
      );
      const data = await res.json();
      if (data.error === 'expired') { setTokenStatus('expired'); return; }
      if (!res.ok || !data.success) throw new Error(data.error || 'Submit failed');
      setTokenStatus('submitted');
    } catch (err) {
      setSubmitError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const set = (key) => (val) => setFormData(f => ({ ...f, [key]: val }));

  const firstName     = surveyMeta?.client_name?.split(' ')[0] || 'your child';
  const guardianFirst = surveyMeta?.guardian_name?.split(' ')[0] || 'there';
  const periodLabel   = surveyMeta?.period_label || '';

  // ── Status screens ────────────────────────────────────────────────────────
  if (tokenStatus === 'loading') {
    return (
      <div style={{ minHeight: '100vh', background: BG, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="loading-spinner" />
      </div>
    );
  }
  if (tokenStatus === 'submitted') {
    return (
      <StatusScreen
        emoji="💙"
        title={`Thank you, ${guardianFirst}!`}
        body={`Your feedback for ${periodLabel} has been received. It makes a real difference to the care we provide for ${firstName} and every family we serve.`}
      />
    );
  }
  if (tokenStatus === 'used') {
    return (
      <StatusScreen
        emoji="✅"
        title="Already submitted"
        body="This survey has already been completed. Thank you for taking the time to share your feedback."
      />
    );
  }
  if (tokenStatus === 'expired') {
    return (
      <StatusScreen
        emoji="⏰"
        title="This link has expired"
        body="Survey links are valid for 30 days. Please contact us if you'd still like to share feedback."
      />
    );
  }
  if (tokenStatus === 'invalid') {
    return (
      <StatusScreen
        emoji="🔗"
        title="Invalid survey link"
        body="This link doesn't look right. Please check the email you received from A Heart For Care or contact us for help."
      />
    );
  }

  const disabled = submitting;

  // ── Main form ─────────────────────────────────────────────────────────────
  return (
    <div style={phoneStyle}>

      {/* ── Header ── */}
      <div style={{ background: NAVY, padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <img src="/logo192.png" alt=""
          onError={e => { e.target.style.display = 'none'; }}
          style={{ width: 44, height: 44, borderRadius: 7, objectFit: 'cover', flexShrink: 0 }} />
        <div>
          <div style={{ fontSize: 14, fontWeight: 500, color: CREAM, lineHeight: 1.2 }}>A Heart For Care</div>
          <div style={{ fontSize: 9, color: 'rgba(235,227,216,0.5)', letterSpacing: '0.08em', marginTop: 2 }}>
            Home care services by Hardshoulder Group
          </div>
        </div>
      </div>

      {/* ── Progress ── */}
      <div style={{ background: NAVY }}>
        <div style={{ fontSize: 10, color: 'rgba(235,227,216,0.5)', textAlign: 'right', padding: '3px 12px 0' }}>
          Monthly care survey — {periodLabel}
        </div>
        <div style={{ background: 'var(--accent)', height: 4 }}>
          <div style={{ background: TERRACOTTA, height: 4, width: '75%' }} />
        </div>
      </div>

      {/* ── Body ── */}
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14, flex: 1 }}>

        {/* Intro card */}
        <div style={{ background: '#fff', borderRadius: 10, border: `0.5px solid ${BORDER}`, padding: '14px 16px' }}>
          <div style={{ fontSize: 14, fontWeight: 500, color: NAVY, marginBottom: 6 }}>
            How are we doing, {guardianFirst}?
          </div>
          <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.6 }}>
            This short survey helps us make sure <strong style={{ color: NAVY }}>{firstName}</strong> is receiving
            the best possible care. Takes about 2 minutes. Your feedback is confidential and shapes how we
            improve our services.
          </div>
        </div>

        {/* ── Section 1: Care quality ── */}
        <SectionCard icon="♥" title="Care quality">
          <QBlock label={`Overall satisfaction with ${firstName}'s care`}>
            <StarRating value={formData.score_overall || 0} onChange={set('score_overall')} disabled={disabled} />
          </QBlock>

          <QBlock label="Does your nurse arrive on time for scheduled visits?">
            <RadioGroup
              value={formData.answer_punctuality}
              onChange={set('answer_punctuality')}
              disabled={disabled}
              options={[
                { val: 'always',    label: 'Always' },
                { val: 'usually',   label: 'Usually' },
                { val: 'sometimes', label: 'Sometimes' },
                { val: 'rarely',    label: 'Rarely' },
              ]}
            />
          </QBlock>

          <QBlock label={`Does your nurse communicate clearly about ${firstName}'s care plan?`}>
            <StarRating value={formData.score_communication || 0} onChange={set('score_communication')} disabled={disabled} />
          </QBlock>
        </SectionCard>

        {/* ── Section 2: Agency & office ── */}
        <SectionCard icon="🏥" title="Agency & office">
          <QBlock label="How easy is it to reach the office when you need help?">
            <StarRating value={formData.score_office_access || 0} onChange={set('score_office_access')} disabled={disabled} />
          </QBlock>

          <QBlock label="Is your assigned nurse consistent (same nurse most visits)?">
            <RadioGroup
              value={formData.answer_nurse_consistent}
              onChange={set('answer_nurse_consistent')}
              disabled={disabled}
              options={[
                { val: 'yes_always', label: 'Yes, always the same nurse' },
                { val: 'mostly_yes', label: 'Mostly yes' },
                { val: 'no_varies',  label: 'No, it varies' },
              ]}
            />
          </QBlock>
        </SectionCard>

        {/* ── Section 3: Your thoughts ── */}
        <SectionCard icon="💬" title="Your thoughts">
          <QBlock label="What is going well that you'd like us to continue?">
            <textarea
              value={formData.feedback_going_well}
              onChange={e => set('feedback_going_well')(e.target.value)}
              disabled={disabled}
              placeholder="e.g. Our nurse always explains everything clearly…"
              style={textareaStyle}
            />
          </QBlock>

          <QBlock label="Is there anything you'd like us to improve?">
            <textarea
              value={formData.feedback_improve}
              onChange={e => set('feedback_improve')(e.target.value)}
              disabled={disabled}
              placeholder="Any feedback is welcome…"
              style={textareaStyle}
            />
          </QBlock>
        </SectionCard>

        {/* ── Section 4: Would you recommend us? ── */}
        <div style={{ background: '#fff', borderRadius: 10, border: `0.5px solid ${BORDER}`, overflow: 'hidden' }}>
          <div style={{ background: NAVY, padding: '10px 14px', fontSize: 12, fontWeight: 500, color: CREAM, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 14 }}>★</span> Would you recommend us?
          </div>
          <div style={{ padding: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 500, color: NAVY, marginBottom: 8, lineHeight: 1.4 }}>
              Would you recommend A Heart For Care to another family?
            </div>
            <RadioGroup
              value={formData.answer_recommend}
              onChange={set('answer_recommend')}
              disabled={disabled}
              options={[
                { val: 'definitely_yes', label: 'Yes — I would highly recommend A Heart For Care' },
                { val: 'probably_yes',   label: 'Probably yes' },
                { val: 'not_sure',       label: 'Not sure' },
                { val: 'no',             label: 'No' },
              ]}
            />
          </div>
        </div>

        {/* Error */}
        {submitError && (
          <div style={{
            background: 'var(--danger-bg)', border: '0.5px solid var(--danger-border)',
            borderRadius: 8, padding: '10px 12px',
            fontSize: 12, color: 'var(--danger)',
          }}>
            {submitError}
          </div>
        )}

        {/* ── Submit area ── */}
        <div style={{ background: '#fff', borderRadius: 10, border: `0.5px solid ${BORDER}`, padding: '16px 14px' }}>
          {/* Primary: submit + open Google review */}
          <button
            onClick={() => handleSubmit(true)}
            disabled={disabled}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              background: NAVY, color: CREAM, border: 'none', borderRadius: 9,
              padding: 14, fontSize: 14, fontWeight: 500, width: '100%',
              cursor: disabled ? 'default' : 'pointer', fontFamily: 'inherit',
              opacity: disabled ? 0.7 : 1, marginBottom: 10,
            }}>
            <GoogleIcon />
            {submitting ? 'Submitting…' : 'Submit & leave a Google review'}
          </button>

          {/* Hint text */}
          <div style={{ fontSize: 11, color: 'var(--text3)', textAlign: 'center', lineHeight: 1.5, marginBottom: 10 }}>
            Submits your feedback and opens Google in a new tab. Takes about 1 minute and helps other
            families in Georgia find trusted care for their children.
          </div>

          {/* Secondary: submit only */}
          <button
            onClick={() => handleSubmit(false)}
            disabled={disabled}
            style={{
              background: 'transparent', color: 'var(--text3)', border: `0.5px solid ${BORDER}`,
              borderRadius: 8, padding: '10px 0', fontSize: 12, width: '100%',
              cursor: disabled ? 'default' : 'pointer', fontFamily: 'inherit',
            }}>
            Submit feedback only
          </button>
        </div>

        {/* Footer */}
        <div style={{
          background: '#fff', border: `0.5px solid ${BORDER}`,
          borderRadius: 10, padding: '14px 16px', textAlign: 'center',
        }}>
          <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.6, marginBottom: 10 }}>
            Thank you for taking the time. Your feedback directly shapes how we care for{' '}
            {firstName} and every child we serve.
          </div>
          <a href={WEBSITE_URL} target="_blank" rel="noreferrer"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
              padding: 10, borderRadius: 8, textDecoration: 'none',
              fontSize: 12, fontWeight: 500, background: 'var(--info-bg)',
              color: NAVY, border: `0.5px solid var(--info-border)`,
            }}>
            🌐 Visit {WEBSITE_HOST.replace(/^www\./, '')}
          </a>
          <div style={{ marginTop: 12, fontSize: 10, color: 'var(--text3)' }}>
            A Heart For Care &nbsp;·&nbsp; Confidential &nbsp;·&nbsp; HIPAA compliant
          </div>
        </div>

      </div>
    </div>
  );
}
